import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../contexts/ToastContext";
import { getBlockedUsersAction, unblockUserAction } from "../../redux/actions/userActions";
import { FaBan, FaUser } from "react-icons/fa";
import CommonLoading from "../loader/CommonLoading";

const BlockingSettings = () => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [unblockingUserId, setUnblockingUserId] = useState(null);

  const blockedUsersList = useSelector((state) => state.user?.blockedUsersList || []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(getBlockedUsersAction());
    } catch (error) {
      console.error("Error loading blocking data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUnblock = async (userId) => {
    if (unblockingUserId) return; // Prevent multiple clicks

    setUnblockingUserId(userId);
    try {
      const result = await dispatch(unblockUserAction(userId));
      if (result?.success) {
        showSuccess("User unblocked successfully");
        // Reload the data
        await loadData();
      } else {
        showError(result?.error || "Failed to unblock user");
      }
    } catch (error) {
      showError("Error unblocking user");
    } finally {
      setUnblockingUserId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center py-12">
          <CommonLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-2 font-roboto-flex">
        Blocking Settings
      </h2>
      <p className="text-sm text-gray-600 mb-4 font-roboto-flex">
        People You Blocked
      </p>

      {/* Content */}
      <div>
        {blockedUsersList.length === 0 ? (
          <div className="text-center py-12 text-gray-500 font-roboto-flex">
            <FaBan className="mx-auto text-4xl mb-4 text-gray-300" />
            <p>You haven't blocked anyone yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {blockedUsersList.map((user) => (
              <div
                key={user._id}
                className="bg-white border rounded-md w-full shadow-2xl shadow-[#f2f5fc] relative hover:shadow-lg hover:border-primary-300 transition-all duration-300"
              >
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <div className="hover:bg-gray-50 rounded-md p-3 -m-3 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <FaUser className="text-gray-400 text-lg" />
                          </div>
                        )}
                        <h4 className="font-semibold text-gray-800 font-heading text-base">{user.name}</h4>
                      </div>
                      <button
                        onClick={() => handleUnblock(user._id)}
                        disabled={unblockingUserId === user._id}
                        className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-roboto-flex font-medium"
                      >
                        {unblockingUserId === user._id ? "Unblocking..." : "Unblock"}
                      </button>
                    </div>
                    {user.email && (
                      <div className="ml-13 pl-1">
                        <span className="text-sm text-gray-600 font-medium font-flex-regular">
                          {user.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockingSettings;
